import React, { useState, useEffect, useCallback } from 'react';
import { leadsAPI, usersAPI } from '../../services/api';
import LeadHistoryModal from '../Admin/LeadHistoryModal';

function ManagerLeadListing() {
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [totalPipelineValue, setTotalPipelineValue] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 12
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    source: 'all',
    assignee: 'all',
    createdBy: 'all',
    dateFrom: '',
    dateTo: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [viewMode, setViewMode] = useState('grid');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState({});
  const [selectedSecondaryAssignees, setSelectedSecondaryAssignees] = useState([]);
  const [statusUpdate, setStatusUpdate] = useState({ 
    status: '', 
    progressPlannedEndDate: '', // When this status should end
    startDate: '', // Overall lead start date
    endDate: '', // Overall lead target end date
    description: '', // Status change description
    personName: '', // Contact person name
    designation: '' // Contact person designation
  });

  const currentUserId = localStorage.getItem('id');

  // Check if manager can modify assignees
  const canModifyAssignees = (lead) => {
    // Manager can modify if they created the lead OR if they are primary/secondary assignee
    return lead.createdBy === currentUserId || 
           lead.primaryAssignee?._id === currentUserId ||
           lead.secondaryAssignees?.some(assignee => assignee._id === currentUserId);
  };

  const LeadStatusOptions = [
    { name: 'Cold Lead', value: 'Cold Lead' },
    { name: 'Prospecting', value: 'Prospecting' },
    { name: 'Qualified', value: 'Qualified' },
    { name: 'Demo Scheduled / Done', value: 'Demo Scheduled / Done' },
    { name: 'Evaluation / Pilot', value: 'Evaluation / Pilot' },
    { name: 'Decision', value: 'Decision' },
    { name: 'Closed Won', value: 'Closed Won' },
    { name: 'Closed Lost', value: 'Closed Lost' },
  ];

  const LeadSourceOptions = [
    { name: 'Website', value: 'Website' },
    { name: 'Event', value: 'Event' },
    { name: 'Referral', value: 'Referral' },
    { name: 'Direct Outreach', value: 'Direct Outreach' },
    { name: 'Marketing Campaign', value: 'Marketing Campaign' },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit
      };
      
      // Handle createdBy filter before cleaning params
      if (filters.createdBy && filters.createdBy !== 'all') {
        params.createdBy = filters.createdBy;
      }
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });
      
      const [leadsResponse, usersResponse] = await Promise.all([
        leadsAPI.getAllLeads(params),
        usersAPI.getAllUsers(),
      ]);
      
      setLeads(leadsResponse.data.leads || leadsResponse.data);
      setTotalPipelineValue(leadsResponse.data.totalPipelineValue || 0);
      if (leadsResponse.data.pagination) {
        setPagination(prev => ({ ...prev, ...leadsResponse.data.pagination }));
      }
      setUsers(usersResponse.data.users || usersResponse.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      source: 'all',
      assignee: 'all',
      createdBy: 'all',
      dateFrom: '',
      dateTo: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const updatePrimaryAssignee = async (id, primaryAssignee) => {
    const lead = leads.find(l => l._id === id);
    if (!canModifyAssignees(lead)) {
      setError('You can only modify assignees for leads you created or are assigned to');
      return;
    }
    
    setLoading(true);
    try {
      await leadsAPI.updateLead(id, { primaryAssignee });
      setSuccess('Primary assignee updated successfully!');
      setError('');
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update primary assignee');
    } finally {
      setLoading(false);
    }
  };

  const updateSecondaryAssignees = async (id, secondaryAssignees) => {
    const lead = leads.find(l => l._id === id);
    if (!canModifyAssignees(lead)) {
      setError('You can only modify assignees for leads you created or are assigned to');
      return;
    }
    
    setLoading(true);
    try {
      await leadsAPI.updateLead(id, { secondaryAssignees });
      setSuccess('Secondary assignees updated successfully!');
      setError('');
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update secondary assignees');
    } finally {
      setLoading(false);
    }
  };

  const openStatusModal = (lead) => {
    setSelectedLead(lead);
    setStatusUpdate({ 
      status: lead.progressStatus, 
      progressPlannedEndDate: lead.progressPlannedEndDate ? new Date(lead.progressPlannedEndDate).toISOString().split('T')[0] : '', 
      startDate: lead.startDate ? new Date(lead.startDate).toISOString().split('T')[0] : '', 
      endDate: lead.endDate ? new Date(lead.endDate).toISOString().split('T')[0] : '', 
      description: '',
      personName: lead.personName || '',
      designation: lead.designation || ''
    });
    setShowStatusModal(true);
  };

  const updateLeadStatus = async () => {
    if (!statusUpdate.description.trim()) {
      setError('Description is required');
      return;
    }
    
    setLoading(true);
    try {
      let archiveReason = '';
      let rating = null;
      let ratingReason = '';

      if (statusUpdate.status === 'Closed Lost') {
        archiveReason = prompt(`Please provide a reason for marking this lead as ${statusUpdate.status}:`);
        if (!archiveReason) {
          setLoading(false);
          return;
        }
      }

      const ratingInput = prompt(`Please provide a rating (0-10) for this status change to ${statusUpdate.status}:`);
      if (ratingInput === null) {
        setLoading(false);
        return;
      }
      rating = parseFloat(ratingInput) || 0;
      
      ratingReason = prompt('Please provide a reason for this rating (optional):') || `Rating for status change to ${statusUpdate.status}`;

      await leadsAPI.updateLead(selectedLead._id, { 
        progressStatus: statusUpdate.status, 
        progressPlannedEndDate: statusUpdate.progressPlannedEndDate,
        startDate: statusUpdate.startDate,
        endDate: statusUpdate.endDate,
        statusDescription: statusUpdate.description,
        personName: statusUpdate.personName,
        designation: statusUpdate.designation,
        archiveReason, 
        rating, 
        ratingReason 
      });
      setSuccess('Lead status updated successfully!');
      setError('');
      setShowStatusModal(false);
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update lead status');
    } finally {
      setLoading(false);
    }
  };

  const openAssigneeModal = (lead) => {
    if (!canModifyAssignees(lead)) {
      setError('You can only modify assignees for leads you created or are assigned to');
      return;
    }
    setSelectedLead(lead);
    setSelectedSecondaryAssignees(lead.secondaryAssignees?.map(a => a._id) || []);
    setShowAssigneeModal(true);
  };

  const handleSecondaryAssigneeChange = (userId) => {
    setSelectedSecondaryAssignees(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const saveSecondaryAssignees = async () => {
    try {
      await updateSecondaryAssignees(selectedLead._id, selectedSecondaryAssignees);
      setShowAssigneeModal(false);
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  const canEditLead = (lead) => {
    // Manager can edit if they created the lead OR if they are assigned to it
    return lead.createdBy === currentUserId || 
           lead.primaryAssignee?._id === currentUserId || 
           lead.secondaryAssignees?.some(assignee => assignee._id === currentUserId);
  };

  const openEditModal = (lead) => {
    // Allow editing if manager created the lead or is assigned to it
    if (!canEditLead(lead)) {
      setError('You can only edit leads you created or are assigned to');
      return;
    }
    
    setSelectedLead(lead);
    setEditData({
      schoolName: lead.schoolName || '',
      personName: lead.personName || '',
      designation: lead.designation || '',
      email: lead.email || '',
      phoneNumber: lead.phoneNumber || '',
      address: lead.address || '',
      expectedValue: lead.expectedValue || 0,
      source: lead.source || 'Website',
      statusDescription: lead.statusDescription || ''
    });
    setShowEditModal(true);
  };

  const updateLeadInfo = async () => {
    setLoading(true);
    try {
      await leadsAPI.updateLead(selectedLead._id, editData);
      setSuccess('Lead information updated successfully!');
      setError('');
      setShowEditModal(false);
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update lead');
    } finally {
      setLoading(false);
    }
  };

  const openLeadHistoryModal = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-4 sm:p-6">
          <div className="flex items-center space-x-3 sm:space-x-4 mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-purple-600 bg-clip-text text-transparent">
                Manager Lead Listing
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1">View and manage your team's leads</p>
            </div>
          </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6">
            {success}
          </div>
        )}

          {/* View Toggle and Filters */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 sm:px-4 py-2 rounded-xl transition-all duration-200 text-sm font-medium ${
                  viewMode === 'grid' 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                    : 'bg-white/60 text-gray-700 hover:bg-white hover:shadow-md border border-gray-200'
                }`}
              >
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Grid View
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 sm:px-4 py-2 rounded-xl transition-all duration-200 text-sm font-medium ${
                  viewMode === 'table' 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg' 
                    : 'bg-white/60 text-gray-700 hover:bg-white hover:shadow-md border border-gray-200'
                }`}
              >
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 6h18m-9 8h9" />
                </svg>
                Table View
              </button>
            </div>
            <div className="text-xs sm:text-sm text-gray-600 bg-white/60 px-3 py-2 rounded-xl border border-gray-200">
              <div className="flex items-center space-x-4">
                <span>Showing {leads.length} leads</span>
                <span className="font-semibold text-green-600">
                  Pipeline: ₹{totalPipelineValue.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 sm:p-6 mb-6 border border-gray-200/50">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by school name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm text-sm"
              />
            </div>
            
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full py-2.5 px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm text-sm"
            >
              <option value="all">All Statuses</option>
              {LeadStatusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.name}
                </option>
              ))}
            </select>
            
            <select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              className="w-full py-2.5 px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm text-sm"
            >
              <option value="all">All Sources</option>
              {LeadSourceOptions.map((source) => (
                <option key={source.value} value={source.value}>
                  {source.name}
                </option>
              ))}
            </select>
            
            <select
              value={filters.assignee}
              onChange={(e) => handleFilterChange('assignee', e.target.value)}
              className="w-full py-2.5 px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm text-sm"
            >
              <option value="all">All Assignees</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.username}
                </option>
              ))}
            </select>

            {/* <select
              value={filters.createdBy}
              onChange={(e) => handleFilterChange('createdBy', e.target.value)}
              className="w-full py-2.5 px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm text-sm"
            >
              <option value="all">All Creators</option>
              <option value={currentUserId}>Created by Me</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  Created by {user.username}
                </option>
              ))}
            </select> */}

            <input
              type="date"
              placeholder="From Date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full py-2.5 px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm text-sm"
            />

            <input
              type="date"
              placeholder="To Date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full py-2.5 px-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm text-sm"
            />
          </div>
          
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <button
                onClick={clearFilters}
                className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-4 py-2.5 rounded-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-200 text-sm font-medium shadow-lg hover:shadow-xl flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Clear Filters</span>
              </button>
            </div>
        </div>

          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-gray-600">Loading leads...</span>
            </div>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200/50 shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                      <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">School Name</th>
                      <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Contact</th>
                      <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                      <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Source</th>
                      <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Value</th>
                      <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Assignees</th>
                      <th className="p-3 sm:p-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {leads.map((lead) => (
                      <tr key={lead._id} className="hover:bg-blue-50/50 transition-colors duration-200 group">
                        <td className="p-3 sm:p-4">
                          <div>
                            <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors duration-200">{lead.schoolName}</div>
                            {lead.createdBy === currentUserId && (
                              <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mt-1 font-medium">
                                Created by you
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 sm:p-4">
                          {lead.personName ? (
                            <div>
                              <div className="font-medium text-gray-900">{lead.personName}</div>
                              {lead.designation && <div className="text-xs text-gray-500">{lead.designation}</div>}
                              <div className="text-xs text-gray-600">{lead.email}</div>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-600">{lead.email}</div>
                          )}
                        </td>
                        <td className="p-3 sm:p-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            lead.progressStatus === 'Closed Won' ? 'bg-green-100 text-green-800' :
                            lead.progressStatus === 'Closed Lost' ? 'bg-red-100 text-red-800' :
                            lead.progressStatus === 'Cold Lead' ? 'bg-gray-100 text-gray-800' :
                            lead.progressStatus === 'Prospecting' ? 'bg-blue-100 text-blue-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {lead.progressStatus}
                          </span>
                        </td>
                        <td className="p-3 sm:p-4 text-sm text-gray-600">{lead.source}</td>
                        <td className="p-3 sm:p-4 font-semibold text-green-600">₹{(lead.expectedValue || 0).toFixed(2)}</td>
                        <td className="p-3 sm:p-4">
                          <div className="space-y-1">
                            {lead.primaryAssignee && (
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span className="text-blue-600 font-medium text-xs">P:</span>
                                <span className="text-xs">{lead.primaryAssignee.username}</span>
                              </div>
                            )}
                            {lead.secondaryAssignees && lead.secondaryAssignees.map((assignee, idx) => (
                              <div key={idx} className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-green-600 font-medium text-xs">S:</span>
                                <span className="text-xs">{assignee.username}</span>
                              </div>
                            ))}
                            {!lead.primaryAssignee && (!lead.secondaryAssignees || lead.secondaryAssignees.length === 0) && (
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                                <span className="text-gray-400 italic text-xs">Unassigned</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-3 sm:p-4">
                          <div className="flex flex-wrap gap-1">
                            <button
                              onClick={() => openLeadHistoryModal(lead)}
                              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 py-1 rounded-lg text-xs hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                            >
                              History
                            </button>
                            <button
                              onClick={() => openStatusModal(lead)}
                              className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-2 py-1 rounded-lg text-xs hover:from-purple-600 hover:to-purple-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
                            >
                              Status
                            </button>
                            <button
                              onClick={() => openAssigneeModal(lead)}
                              disabled={!canModifyAssignees(lead)}
                              className={`px-2 py-1 rounded-lg text-xs transition-all duration-200 font-medium shadow-sm hover:shadow-md ${
                                canModifyAssignees(lead)
                                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              }`}
                            >
                              Assign
                            </button>
                            <button
                              onClick={() => openEditModal(lead)}
                              className={`px-2 py-1 rounded-lg text-xs transition-all duration-200 font-medium shadow-sm hover:shadow-md ${
                                canEditLead(lead) 
                                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700' 
                                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              }`}
                              disabled={!canEditLead(lead)}
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {leads.map((lead) => (
              <div key={lead._id} className="bg-white/80 backdrop-blur-sm border border-gray-200/50 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 group cursor-pointer">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300 truncate">{lead.schoolName}</h3>
                    {lead.personName && (
                      <p className="text-xs sm:text-sm text-gray-700 font-medium truncate">
                        {lead.personName} {lead.designation && `(${lead.designation})`}
                      </p>
                    )}
                    <p className="text-xs sm:text-sm text-gray-600 truncate">{lead.email}</p>
                    {lead.createdBy === currentUserId && (
                      <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mt-2 font-medium">
                        Created by you
                      </span>
                    )}
                </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                    lead.progressStatus === 'Closed Won' ? 'bg-green-100 text-green-800' :
                    lead.progressStatus === 'Closed Lost' ? 'bg-red-100 text-red-800' :
                    lead.progressStatus === 'Cold Lead' ? 'bg-gray-100 text-gray-800' :
                    lead.progressStatus === 'Prospecting' ? 'bg-blue-100 text-blue-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {lead.progressStatus}
                  </span>
              </div>

                <div className="space-y-2 mb-4">
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="truncate">{lead.phoneNumber}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span className="truncate">{lead.source}</span>
                    </div>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-700">Assignees:</span>
                      <span className="font-semibold text-green-600">₹{(lead.expectedValue || 0).toFixed(2)}</span>
                    </div>
                    <div className="space-y-1">
                      {lead.primaryAssignee && (
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-blue-600 font-medium text-xs">P:</span>
                          <span className="truncate text-xs">{lead.primaryAssignee.username}</span>
                        </div>
                      )}
                      {lead.secondaryAssignees && lead.secondaryAssignees.map((assignee, idx) => (
                        <div key={idx} className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-green-600 font-medium text-xs">S:</span>
                          <span className="truncate text-xs">{assignee.username}</span>
                        </div>
                      ))}
                      {!lead.primaryAssignee && (!lead.secondaryAssignees || lead.secondaryAssignees.length === 0) && (
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                          <span className="text-gray-400 italic text-xs">Unassigned</span>
                        </div>
                      )}
                    </div>
                  </div>
              </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2">
                      <label className="text-xs font-medium text-gray-700 flex-shrink-0">Primary:</label>
                      <select
                        value={lead.primaryAssignee?._id || ''}
                        onChange={(e) => updatePrimaryAssignee(lead._id, e.target.value)}
                        disabled={!canModifyAssignees(lead)}
                        className={`text-xs px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 flex-1 bg-white/80 ${
                          !canModifyAssignees(lead) ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        <option value="">None</option>
                        {users.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.username}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-xs font-medium text-gray-700 flex-shrink-0">Secondary:</label>
                      <button
                        onClick={() => openAssigneeModal(lead)}
                        disabled={!canModifyAssignees(lead)}
                        className={`text-xs px-2 py-1 rounded-lg transition-all duration-200 flex-1 font-medium ${
                          canModifyAssignees(lead)
                            ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        Manage ({lead.secondaryAssignees?.length || 0})
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openLeadHistoryModal(lead)}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 sm:px-3 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-xs sm:text-sm font-medium shadow-md hover:shadow-lg"
                    >
                      <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      History
                    </button>
                    <button
                      onClick={() => openStatusModal(lead)}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white px-2 sm:px-3 py-2 rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-200 text-xs sm:text-sm font-medium shadow-md hover:shadow-lg"
                    >
                      <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Status
                    </button>
                    <button
                      onClick={() => openEditModal(lead)}
                      className={`flex-1 px-2 sm:px-3 py-2 rounded-lg transition-all duration-200 text-xs sm:text-sm font-medium shadow-md hover:shadow-lg ${
                        canEditLead(lead) 
                          ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                      disabled={!canEditLead(lead)}
                    >
                      <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && leads.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 bg-white/60 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
              <p className="text-gray-500">Try adjusting your filters or create a new lead.</p>
            </div>
          )}

        {/* Modals */}
        {selectedLead && showLeadModal && (
          <LeadHistoryModal
            lead={selectedLead}
            onClose={() => setShowLeadModal(false)}
            users={users}
            onUpdateHistory={async () => {
              setSuccess('History updated successfully!');
              await fetchData();
              setTimeout(() => setSuccess(''), 3000);
            }}
          />
        )}
        
        {/* Status Update Modal */}
        {showStatusModal && selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Update Lead Status</h3>
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={statusUpdate.status}
                      onChange={(e) => setStatusUpdate({...statusUpdate, status: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {LeadStatusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status End Date</label>
                    <input
                      type="date"
                      value={statusUpdate.progressPlannedEndDate}
                      onChange={(e) => setStatusUpdate({...statusUpdate, progressPlannedEndDate: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                    <input
                      type="text"
                      value={statusUpdate.personName}
                      onChange={(e) => setStatusUpdate({...statusUpdate, personName: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Contact person name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                    <input
                      type="text"
                      value={statusUpdate.designation}
                      onChange={(e) => setStatusUpdate({...statusUpdate, designation: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Principal, Director, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Lead Start Date</label>
                    <input
                      type="date"
                      value={statusUpdate.startDate}
                      onChange={(e) => setStatusUpdate({...statusUpdate, startDate: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Target End Date</label>
                    <input
                      type="date"
                      value={statusUpdate.endDate}
                      onChange={(e) => setStatusUpdate({...statusUpdate, endDate: e.target.value})}
                      className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500 ml-1" title="This field is required">
                      <svg className="w-3 h-3 inline-block" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </span>
                  </label>
                  <textarea
                    value={statusUpdate.description}
                    onChange={(e) => setStatusUpdate({...statusUpdate, description: e.target.value})}
                    className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-20"
                    placeholder="Describe the status change..."
                    required
                  />
                  {!statusUpdate.description.trim() && (
                    <p className="text-red-500 text-xs mt-1">This field is required</p>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={updateLeadStatus}
                    disabled={loading}
                    className="flex-1 bg-blue-500 text-white px-4 py-2.5 rounded-lg hover:bg-blue-600 transition disabled:opacity-50 font-medium"
                  >
                    {loading ? 'Updating...' : 'Update Status'}
                  </button>
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Secondary Assignee Modal */}
        {showAssigneeModal && selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">Manage Secondary Assignees</h3>
                <button
                  onClick={() => setShowAssigneeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                {users.filter(user => user._id !== selectedLead.primaryAssignee?._id).map((user) => (
                  <label key={user._id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={selectedSecondaryAssignees.includes(user._id)}
                      onChange={() => handleSecondaryAssigneeChange(user._id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{user.username}</span>
                  </label>
                ))}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={saveSecondaryAssignees}
                  disabled={loading}
                  className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setShowAssigneeModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Edit Lead Modal - Same as Admin but only for own leads */}
        {showEditModal && selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold">Edit Lead Information</h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">School Name</label>
                    <input
                      type="text"
                      value={editData.schoolName}
                      onChange={(e) => setEditData({...editData, schoolName: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                    <input
                      type="text"
                      value={editData.personName}
                      onChange={(e) => setEditData({...editData, personName: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Designation</label>
                    <input
                      type="text"
                      value={editData.designation}
                      onChange={(e) => setEditData({...editData, designation: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({...editData, email: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={editData.phoneNumber}
                      onChange={(e) => setEditData({...editData, phoneNumber: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <input
                      type="text"
                      value={editData.address}
                      onChange={(e) => setEditData({...editData, address: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expected Value</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editData.expectedValue}
                      onChange={(e) => setEditData({...editData, expectedValue: parseFloat(e.target.value) || 0})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Source</label>
                    <select
                      value={editData.source}
                      onChange={(e) => setEditData({...editData, source: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {LeadSourceOptions.map((source) => (
                        <option key={source.value} value={source.value}>
                          {source.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status Description</label>
                    <input
                      type="text"
                      value={editData.statusDescription}
                      onChange={(e) => setEditData({...editData, statusDescription: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Optional description for current status"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={updateLeadInfo}
                    disabled={loading}
                    className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Lead'}
                  </button>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export default ManagerLeadListing;